import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Menu, Save, Image, Type, Link2, Palette, Plus, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminSidebar from '../../components/AdminSidebar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminHomepage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [heroData, setHeroData] = useState({
    headline: '',
    subheadline: '',
    background_image: '',
    cta_primary_text: '',
    cta_primary_link: '',
    cta_secondary_text: '',
    cta_secondary_link: '',
    overlay_opacity: 0.6,
    is_active: true
  });

  const [trustBadges, setTrustBadges] = useState([]);
  const [festiveBanner, setFestiveBanner] = useState({
    title: '',
    subtitle: '',
    background_image: '',
    cta_text: '',
    cta_link: '',
    background_color: '#800000',
    text_color: '#FFFFFF',
    is_active: true
  });

  const [whyChoose, setWhyChoose] = useState({
    title: '',
    subtitle: '',
    items: []
  });

  const [newsletter, setNewsletter] = useState({
    title: '',
    subtitle: '',
    placeholder_text: '',
    button_text: '',
    background_color: '#FEF3C7',
    discount_text: ''
  });

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    fetchAllData();
  }, [navigate]);

  const fetchAllData = async () => {
    try {
      const [heroRes, trustRes, festiveRes, whyRes, newsRes] = await Promise.all([
        axios.get(`${API_URL}/api/cms/hero`),
        axios.get(`${API_URL}/api/cms/trust-badges`),
        axios.get(`${API_URL}/api/cms/festive-banner`),
        axios.get(`${API_URL}/api/cms/why-choose`),
        axios.get(`${API_URL}/api/cms/newsletter`),
      ]);

      setHeroData(heroRes.data);
      setTrustBadges(trustRes.data.badges || []);
      setFestiveBanner(festiveRes.data);
      setWhyChoose(whyRes.data);
      setNewsletter(newsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load homepage data');
    } finally {
      setLoading(false);
    }
  };

  const saveHero = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/hero`, heroData);
      toast.success('Hero section saved!');
    } catch (error) {
      toast.error('Failed to save hero section');
    } finally {
      setSaving(false);
    }
  };

  const saveTrustBadges = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/trust-badges`, { badges: trustBadges });
      toast.success('Trust badges saved!');
    } catch (error) {
      toast.error('Failed to save trust badges');
    } finally {
      setSaving(false);
    }
  };

  const saveFestiveBanner = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/festive-banner`, festiveBanner);
      toast.success('Festive banner saved!');
    } catch (error) {
      toast.error('Failed to save festive banner');
    } finally {
      setSaving(false);
    }
  };

  const saveWhyChoose = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/why-choose`, whyChoose);
      toast.success('Why Choose section saved!');
    } catch (error) {
      toast.error('Failed to save Why Choose section');
    } finally {
      setSaving(false);
    }
  };

  const saveNewsletter = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cms/newsletter`, newsletter);
      toast.success('Newsletter section saved!');
    } catch (error) {
      toast.error('Failed to save newsletter section');
    } finally {
      setSaving(false);
    }
  };

  const addTrustBadge = () => {
    setTrustBadges([...trustBadges, {
      id: `badge_${Date.now()}`,
      icon: 'Shield',
      title: 'New Badge',
      is_active: true,
      order: trustBadges.length + 1
    }]);
  };

  const addWhyChooseItem = () => {
    setWhyChoose(prev => ({
      ...prev,
      items: [...(prev.items || []), {
        id: `item_${Date.now()}`,
        icon: 'CheckCircle',
        title: 'New Feature',
        description: 'Description here',
        order: (prev.items?.length || 0) + 1
      }]
    }));
  };

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'trust', label: 'Trust Badges' },
    { id: 'festive', label: 'Festive Banner' },
    { id: 'why', label: 'Why Choose Us' },
    { id: 'newsletter', label: 'Newsletter' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-maroon"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminSidebar active="homepage" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b border-gold/10 px-4 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-heading font-bold text-maroon">Homepage Manager</h1>
          </div>
          <a 
            href="/" 
            target="_blank" 
            className="text-sm text-maroon hover:underline"
          >
            View Site →
          </a>
        </header>

        <main className="p-4 md:p-6">
          <div className="flex flex-wrap gap-2 mb-6 bg-white rounded-xl p-2 border border-gold/10">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-maroon text-white'
                    : 'text-maroon hover:bg-cream'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Hero Section Tab */}
          {activeTab === 'hero' && (
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <h2 className="text-lg font-heading font-semibold text-maroon mb-6">Hero Section</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Headline</label>
                  <Input
                    value={heroData.headline}
                    onChange={(e) => setHeroData(prev => ({ ...prev, headline: e.target.value }))}
                    placeholder="Sacred Puja Essentials..."
                    className="border-maroon/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Subheadline</label>
                  <Input
                    value={heroData.subheadline}
                    onChange={(e) => setHeroData(prev => ({ ...prev, subheadline: e.target.value }))}
                    placeholder="Authentic | Premium Quality..."
                    className="border-maroon/20"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Background Image URL</label>
                  <Input
                    value={heroData.background_image}
                    onChange={(e) => setHeroData(prev => ({ ...prev, background_image: e.target.value }))}
                    placeholder="https://..."
                    className="border-maroon/20"
                  />
                  {heroData.background_image && (
                    <img src={heroData.background_image} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Primary CTA Text</label>
                  <Input
                    value={heroData.cta_primary_text}
                    onChange={(e) => setHeroData(prev => ({ ...prev, cta_primary_text: e.target.value }))}
                    placeholder="Shop Now"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Primary CTA Link</label>
                  <Input
                    value={heroData.cta_primary_link}
                    onChange={(e) => setHeroData(prev => ({ ...prev, cta_primary_link: e.target.value }))}
                    placeholder="/shop"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Secondary CTA Text</label>
                  <Input
                    value={heroData.cta_secondary_text}
                    onChange={(e) => setHeroData(prev => ({ ...prev, cta_secondary_text: e.target.value }))}
                    placeholder="Explore Collection"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Secondary CTA Link</label>
                  <Input
                    value={heroData.cta_secondary_link}
                    onChange={(e) => setHeroData(prev => ({ ...prev, cta_secondary_link: e.target.value }))}
                    placeholder="/shop?collection=festive"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Overlay Opacity ({heroData.overlay_opacity})</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={heroData.overlay_opacity}
                    onChange={(e) => setHeroData(prev => ({ ...prev, overlay_opacity: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                </div>
              </div>

              <Button
                onClick={saveHero}
                disabled={saving}
                className="mt-6 bg-maroon hover:bg-maroon-dark text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Hero Section'}
              </Button>
            </div>
          )}

          {/* Trust Badges Tab */}
          {activeTab === 'trust' && (
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-semibold text-maroon">Trust Badges</h2>
                <Button onClick={addTrustBadge} className="bg-maroon hover:bg-maroon-dark text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Badge
                </Button>
              </div>

              <div className="space-y-4">
                {trustBadges.map((badge, index) => (
                  <div key={badge.id} className="flex items-center gap-4 p-4 bg-cream rounded-xl">
                    <GripVertical className="w-5 h-5 text-maroon/30 cursor-grab" />
                    <Input
                      value={badge.icon}
                      onChange={(e) => {
                        const updated = [...trustBadges];
                        updated[index].icon = e.target.value;
                        setTrustBadges(updated);
                      }}
                      placeholder="Icon name"
                      className="w-32 border-maroon/20"
                    />
                    <Input
                      value={badge.title}
                      onChange={(e) => {
                        const updated = [...trustBadges];
                        updated[index].title = e.target.value;
                        setTrustBadges(updated);
                      }}
                      placeholder="Badge title"
                      className="flex-1 border-maroon/20"
                    />
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const updated = [...trustBadges];
                        updated[index].is_active = !updated[index].is_active;
                        setTrustBadges(updated);
                      }}
                      className={badge.is_active ? 'text-green-600' : 'text-gray-400'}
                    >
                      {badge.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setTrustBadges(trustBadges.filter((_, i) => i !== index))}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                onClick={saveTrustBadges}
                disabled={saving}
                className="mt-6 bg-maroon hover:bg-maroon-dark text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Trust Badges'}
              </Button>
            </div>
          )}

          {/* Festive Banner Tab */}
          {activeTab === 'festive' && (
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-semibold text-maroon">Festive Banner</h2>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={festiveBanner.is_active}
                    onChange={(e) => setFestiveBanner(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-maroon">Active</span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Title</label>
                  <Input
                    value={festiveBanner.title}
                    onChange={(e) => setFestiveBanner(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Diwali Special"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Subtitle</label>
                  <Input
                    value={festiveBanner.subtitle}
                    onChange={(e) => setFestiveBanner(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Illuminate your home..."
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">CTA Text</label>
                  <Input
                    value={festiveBanner.cta_text}
                    onChange={(e) => setFestiveBanner(prev => ({ ...prev, cta_text: e.target.value }))}
                    placeholder="Shop Now"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">CTA Link</label>
                  <Input
                    value={festiveBanner.cta_link}
                    onChange={(e) => setFestiveBanner(prev => ({ ...prev, cta_link: e.target.value }))}
                    placeholder="/shop?collection=festive"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={festiveBanner.background_color}
                      onChange={(e) => setFestiveBanner(prev => ({ ...prev, background_color: e.target.value }))}
                      className="w-12 h-10 rounded border"
                    />
                    <Input
                      value={festiveBanner.background_color}
                      onChange={(e) => setFestiveBanner(prev => ({ ...prev, background_color: e.target.value }))}
                      className="flex-1 border-maroon/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={festiveBanner.text_color}
                      onChange={(e) => setFestiveBanner(prev => ({ ...prev, text_color: e.target.value }))}
                      className="w-12 h-10 rounded border"
                    />
                    <Input
                      value={festiveBanner.text_color}
                      onChange={(e) => setFestiveBanner(prev => ({ ...prev, text_color: e.target.value }))}
                      className="flex-1 border-maroon/20"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={saveFestiveBanner}
                disabled={saving}
                className="mt-6 bg-maroon hover:bg-maroon-dark text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Festive Banner'}
              </Button>
            </div>
          )}

          {/* Why Choose Tab */}
          {activeTab === 'why' && (
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-heading font-semibold text-maroon">Why Choose Us</h2>
                <Button onClick={addWhyChooseItem} className="bg-maroon hover:bg-maroon-dark text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Section Title</label>
                  <Input
                    value={whyChoose.title}
                    onChange={(e) => setWhyChoose(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Why Choose Indicraft"
                    className="border-maroon/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Subtitle</label>
                  <Input
                    value={whyChoose.subtitle}
                    onChange={(e) => setWhyChoose(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Experience the divine difference"
                    className="border-maroon/20"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {(whyChoose.items || []).map((item, index) => (
                  <div key={item.id} className="flex items-start gap-4 p-4 bg-cream rounded-xl">
                    <GripVertical className="w-5 h-5 text-maroon/30 cursor-grab mt-2" />
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        value={item.icon}
                        onChange={(e) => {
                          const updated = [...whyChoose.items];
                          updated[index].icon = e.target.value;
                          setWhyChoose(prev => ({ ...prev, items: updated }));
                        }}
                        placeholder="Icon"
                        className="border-maroon/20"
                      />
                      <Input
                        value={item.title}
                        onChange={(e) => {
                          const updated = [...whyChoose.items];
                          updated[index].title = e.target.value;
                          setWhyChoose(prev => ({ ...prev, items: updated }));
                        }}
                        placeholder="Title"
                        className="border-maroon/20"
                      />
                      <Input
                        value={item.description}
                        onChange={(e) => {
                          const updated = [...whyChoose.items];
                          updated[index].description = e.target.value;
                          setWhyChoose(prev => ({ ...prev, items: updated }));
                        }}
                        placeholder="Description"
                        className="border-maroon/20"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const updated = whyChoose.items.filter((_, i) => i !== index);
                        setWhyChoose(prev => ({ ...prev, items: updated }));
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button
                onClick={saveWhyChoose}
                disabled={saving}
                className="mt-6 bg-maroon hover:bg-maroon-dark text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Why Choose Section'}
              </Button>
            </div>
          )}

          {/* Newsletter Tab */}
          {activeTab === 'newsletter' && (
            <div className="bg-white rounded-2xl p-6 border border-gold/10">
              <h2 className="text-lg font-heading font-semibold text-maroon mb-6">Newsletter Section</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Title</label>
                  <Input
                    value={newsletter.title}
                    onChange={(e) => setNewsletter(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Join Our Divine Community"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Subtitle</label>
                  <Input
                    value={newsletter.subtitle}
                    onChange={(e) => setNewsletter(prev => ({ ...prev, subtitle: e.target.value }))}
                    placeholder="Subscribe for exclusive offers..."
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Placeholder Text</label>
                  <Input
                    value={newsletter.placeholder_text}
                    onChange={(e) => setNewsletter(prev => ({ ...prev, placeholder_text: e.target.value }))}
                    placeholder="Enter your email"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Button Text</label>
                  <Input
                    value={newsletter.button_text}
                    onChange={(e) => setNewsletter(prev => ({ ...prev, button_text: e.target.value }))}
                    placeholder="Subscribe"
                    className="border-maroon/20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Background Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newsletter.background_color}
                      onChange={(e) => setNewsletter(prev => ({ ...prev, background_color: e.target.value }))}
                      className="w-12 h-10 rounded border"
                    />
                    <Input
                      value={newsletter.background_color}
                      onChange={(e) => setNewsletter(prev => ({ ...prev, background_color: e.target.value }))}
                      className="flex-1 border-maroon/20"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-maroon/70 mb-1">Discount Text (Badge)</label>
                  <Input
                    value={newsletter.discount_text}
                    onChange={(e) => setNewsletter(prev => ({ ...prev, discount_text: e.target.value }))}
                    placeholder="Get 10% OFF on your first order!"
                    className="border-maroon/20"
                  />
                </div>
              </div>

              <Button
                onClick={saveNewsletter}
                disabled={saving}
                className="mt-6 bg-maroon hover:bg-maroon-dark text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Newsletter Section'}
              </Button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminHomepage;
